import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SetupStep, DesignStep, ReviewStep } from "@/components/CampaignSteps";
import { TEMPLATE_BLOCKS, blocksHaveContent, blocksToText } from "@/components/EmailBlocks";
import { api, errMsg } from "@/lib/api";
import { DRIP_PRESET } from "@/lib/crm";

const START = { type: "email", name: "", from_name: "AVY Team", from_email: "hello@avy.com", recipient_ids: [], template: "", subject: "", blocks: [], mode: "now", date: "", time: "09:00", steps: DRIP_PRESET.steps };
const STEPS = ["Setup", "Design", "Review & Send"];

export default function CampaignWizard({ open, onOpenChange, contacts, initialIds, onCreated }) {
  const [f, setF] = useState(START);
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((s) => ({ ...s, ...p }));

  useEffect(() => {
    if (open) { setF({ ...START, recipient_ids: initialIds || [], blocks: TEMPLATE_BLOCKS.blank() }); setStep(0); }
  }, [open, initialIds]);

  const preview = contacts.find((c) => c.id === f.recipient_ids[0]);

  const next = () => {
    if (step === 0) {
      if (!f.name.trim()) return toast.error("Enter a campaign name");
      if (!f.from_name.trim() || !f.from_email.trim()) return toast.error("Enter the sender name and email");
      if (!f.recipient_ids.length) return toast.error("Select at least one recipient");
    }
    if (step === 1) {
      if (f.type === "email" && (!f.subject.trim() || !blocksHaveContent(f.blocks))) return toast.error("Add a subject and some email content");
      if (f.type === "drip" && f.steps.some((s) => !s.subject.trim())) return toast.error("Every email in the sequence needs a subject");
    }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    const scheduled_at = f.mode === "schedule" ? (f.date && f.time ? `${f.date}T${f.time}` : null) : null;
    if (f.mode === "schedule" && !scheduled_at) return toast.error("Select a date and time");
    setBusy(true);
    try {
      const isEmail = f.type === "email";
      const payload = { type: f.type, name: f.name, from_name: f.from_name, from_email: f.from_email, recipient_ids: f.recipient_ids, template: f.template, subject: f.subject, body: isEmail ? blocksToText(f.blocks) : "", blocks: isEmail ? f.blocks : [], mode: f.mode, scheduled_at, steps: isEmail ? [] : f.steps };
      const { data } = await api.post("/campaigns", payload);
      let camp = data;
      if (f.type === "drip" && f.mode !== "draft") {
        camp = (await api.post(`/campaigns/${data.id}/activate`, { start_at: scheduled_at ? new Date(scheduled_at).toISOString() : null })).data;
      }
      toast.success(`${camp.name} · ${camp.status}`);
      onOpenChange(false);
      onCreated();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`${step === 1 && f.type === "email" ? "max-w-5xl" : "max-w-2xl"} max-h-[92vh] overflow-y-auto`} data-testid="campaign-wizard">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Create Campaign</DialogTitle>
          <DialogDescription className="sr-only">Campaign setup, design and send options</DialogDescription>
          <div className="flex gap-2 pt-2 text-xs">
            {STEPS.map((s, i) => (
              <span key={s} className={`rounded-full px-3 py-1 font-semibold ${step === i ? "bg-[#3B185F] text-white" : step > i ? "bg-[#D4AF37] text-[#19082B]" : "bg-stone-100 text-stone-500"}`} data-testid={`campaign-step-${i}`}>{i + 1}. {s}</span>
            ))}
          </div>
        </DialogHeader>
        {step === 0 && <SetupStep f={f} set={set} contacts={contacts} />}
        {step === 1 && <DesignStep f={f} set={set} preview={preview} />}
        {step === 2 && <ReviewStep f={f} set={set} preview={preview} recipientsCount={f.recipient_ids.length} />}
        <div className="flex justify-between pt-2">
          <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="campaign-back-btn">Back</Button>
          {step < 2
            ? <Button onClick={next} className="bg-[#3B185F]" data-testid="campaign-next-btn">Continue</Button>
            : <Button disabled={busy} onClick={submit} className="bg-[#D4AF37] text-[#19082B] hover:bg-[#C59B27]" data-testid="campaign-submit-btn">
                {busy ? "Saving…" : f.mode === "draft" ? "Save Draft" : f.mode === "schedule" ? "Schedule" : f.type === "drip" ? "Activate Sequence" : "Send Now"}
              </Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
